import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { InputText } from 'primereact/inputtext';

import { useConferenceRoomAvailability } from 'hooks/useConferenceRoomAvailability';
import LabelName from 'components/UI/LabelName/LabelName';
import AddLocationForm from './AddLocationForm';
import LocationSelect from 'components/UI/LocationSelect';

function EventLocation({ control, setValue, watch, editEventModal, errors, trigger }) {
  const [addLocation, setAddLocation] = useState(false);

  // Watch form values
  const selectedLocation = watch('location');
  const meetingStartTime = watch('meeting_start_time');
  const meetingEndTime = watch('meeting_end_time');
  const whenValue = watch('when');
  const till = watch('till');
  const allDay = watch('all_day');
  const isRecurring = watch('is_recurring');
  const pk = watch('pk');

  // Check if selected location is an office location
  const isOfficeLocation = Boolean(!selectedLocation?.is_contact);

  // Use the availability hook
  const currentEventId = editEventModal && pk ? pk?.split('#')?.[1] : null;
  const { conferenceRooms, isLoading, isCheckingAvailability, error } = useConferenceRoomAvailability(
    isOfficeLocation ? selectedLocation?.value : null,
    allDay ? whenValue : meetingStartTime,
    allDay ? till : meetingEndTime,
    {
      autoCheckAvailability: false,
      checkOnTimeChange: true, // Auto-check when time/date changes
      isAllDay: allDay,
    },
    currentEventId,
    whenValue
  );

  useEffect(() => {
    if (trigger && isOfficeLocation) {
      trigger('conference_room_id');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conferenceRooms, isOfficeLocation]);

  return (
    <>
      <div className="d-flex py-2 flex-wrap">
        <div className="col-md-4 col-12 mt-2">
          <LabelName required={false} htmlFor="location">
            Location
          </LabelName>
        </div>
        <div className="col-md-7 col-11 align-items-center">
          {addLocation ? (
            <AddLocationForm setAddLocation={setAddLocation} setValue={setValue} />
          ) : (
            <Controller
              name="location"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <LocationSelect
                  id={field.name}
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e);
                    if (e?.is_contact) {
                      setValue('conference_room_id', '');
                    }
                  }}
                  placeholder="Select Location"
                  className="w-100"
                  isSearchable={true}
                />
              )}
            />
          )}
        </div>
        <div className="col-sm-1 d-flex justify-content-end mt-2 ps-2">
          <span className="pointer">
            <i className="fas fa-plus-circle location-add" onClick={() => setAddLocation(true)}></i>
            <Tooltip content="Add Location" position="top" target=".location-add" showDelay={500} />
          </span>
        </div>
      </div>

      {/* Video Conference URL */}
      <div className="d-flex align-items-center py-2 flex-wrap">
        <div className="col-md-4 col-12">
          <LabelName required={false} htmlFor="conference_url">
            Video Conference URL
          </LabelName>
        </div>
        <div className="col-md-8 col-12">
          <Controller
            name="conference_url"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <InputText
                type="text"
                id={field.name}
                {...field}
                className="w-100 input-shadow"
                placeholder="https://zoom.us/j/... or https://meet.google.com/..."
              />
            )}
          />
        </div>
      </div>

      {/* Conference Room - hidden for recurring events */}
      {selectedLocation && isOfficeLocation && !isRecurring && (
        <div className="d-flex py-2 flex-wrap">
          <div className="col-md-4 col-12 mt-2">
            <LabelName required={false} htmlFor="conference_room_id">
              Conference Room
            </LabelName>
          </div>
          <div className="col-md-8 col-12 align-items-center">
            <Controller
              name="conference_room_id"
              control={control}
              defaultValue=""
              rules={{
                validate: (value) => {
                  const selectedRoom = conferenceRooms.find((room) => room.value === value);
                  if (selectedRoom && selectedRoom?.isAvailable === false) {
                    return 'This room is not available for the selected time slot';
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <div className="position-relative">
                  <Dropdown
                    id={field.name}
                    value={field.value}
                    onChange={(e) => {
                      const selectedRoom = conferenceRooms.find((room) => room.value === e.value);
                      if (selectedRoom && selectedRoom.disabled) {
                        return;
                      }
                      field.onChange(e.value);
                    }}
                    options={conferenceRooms}
                    placeholder={
                      isLoading ? 'Loading rooms...' : isCheckingAvailability ? 'Checking availability...' : 'Select Conference Room'
                    }
                    className="w-100"
                    filter
                    showClear={field.value ? true : false}
                    optionLabel="label"
                    optionValue="value"
                    disabled={isLoading || isCheckingAvailability}
                    optionDisabled="disabled"
                    itemTemplate={(option) => (
                      <div
                        style={{
                          color: option.disabled ? '#999' : 'inherit',
                          fontStyle: option.disabled ? 'italic' : 'normal',
                        }}
                      >
                        {option.dropdownLabel || option.label}
                      </div>
                    )}
                    dropdownIcon={isCheckingAvailability || isLoading ? 'pi pi-spin pi-spinner' : undefined}
                  />
                  {errors.conference_room_id && <small className="p-error mt-1 d-block">{errors.conference_room_id.message}</small>}
                </div>
              )}
            />
            {error && <small className="text-warning mt-1 d-block">{error}</small>}
          </div>
        </div>
      )}
    </>
  );
}

export default EventLocation;
